import { ArrowUpRight } from "lucide-react";

import { useLang, useLangPrefix } from "@/contexts/lang";

export default function Footer() {
	const currentYear = new Date().getFullYear();
	const t = useLang();
	const prefix = useLangPrefix();

	const addressLines = t.footer.address.split("\n");

	return (
		<footer className="bg-primary text-white py-16 sm:py-20 md:py-24 border-t border-white/[0.04] mt-grain">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				{/* Top section */}
				<div className="grid gap-12 md:gap-16 lg:grid-cols-12 lg:gap-8">
					{/* Brand column */}
					<div className="lg:col-span-4">
						<a href={prefix || "/"} className="inline-block group">
							<span className="font-display text-xl sm:text-2xl font-normal tracking-wide text-white block transition-opacity duration-500 group-hover:opacity-80">
								MAKEDONIJATURIST
							</span>
						</a>
						<div className="mt-5 flex items-center gap-4">
							<span className="h-px w-10 bg-accent/40" />
							<p className="text-sm text-white/60">{t.footer.tagline}</p>
						</div>
						<p className="mt-4 text-sm text-white/50">{t.footer.hospitality}</p>
					</div>

					{/* Links columns */}
					<div className="lg:col-span-8">
						<div className="grid gap-8 sm:gap-12 sm:grid-cols-3">
							{/* Explore */}
							<div>
								<h3 className="text-label text-accent/70 mb-5">
									{t.footer.explore}
								</h3>
								<ul className="space-y-3.5 text-sm">
									<li>
										<a
											href={`${prefix}/#about`}
											className="group inline-flex items-center gap-1.5 text-white/65 transition-colors duration-300 hover:text-white cursor-pointer"
										>
											{t.footer.aboutUs}
											<ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:opacity-100" />
										</a>
									</li>
									<li>
										<a
											href={`${prefix}/#portfolio`}
											className="group inline-flex items-center gap-1.5 text-white/65 transition-colors duration-300 hover:text-white cursor-pointer"
										>
											{t.footer.ourHotels}
											<ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:opacity-100" />
										</a>
									</li>
									<li>
										<a
											href={`${prefix}/#partners`}
											className="group inline-flex items-center gap-1.5 text-white/65 transition-colors duration-300 hover:text-white cursor-pointer"
										>
											{t.footer.partners}
											<ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:opacity-100" />
										</a>
									</li>
									<li>
										<a
											href={`${prefix}/#contact`}
											className="group inline-flex items-center gap-1.5 text-white/65 transition-colors duration-300 hover:text-white cursor-pointer"
										>
											{t.footer.contact}
											<ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:opacity-100" />
										</a>
									</li>
								</ul>
							</div>

							{/* Legal */}
							<div>
								<h3 className="text-label text-accent/70 mb-5">
									{t.footer.legal}
								</h3>
								<ul className="space-y-3.5 text-sm">
									<li>
										<a
											href={`${prefix}/privacy-policy`}
											className="group inline-flex items-center gap-1.5 text-white/65 transition-colors duration-300 hover:text-white cursor-pointer"
										>
											{t.footer.privacy}
											<ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:opacity-100" />
										</a>
									</li>
									<li>
										<a
											href={`${prefix}/terms-of-service`}
											className="group inline-flex items-center gap-1.5 text-white/65 transition-colors duration-300 hover:text-white cursor-pointer"
										>
											{t.footer.terms}
											<ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:opacity-100" />
										</a>
									</li>
								</ul>
							</div>

							{/* Office */}
							<div>
								<h3 className="text-label text-accent/70 mb-5">
									{t.footer.office}
								</h3>
								<address className="text-sm text-white/65 not-italic leading-relaxed">
									{addressLines.map((line, i) => (
										<span key={line}>
											{line}
											{i < addressLines.length - 1 && <br />}
										</span>
									))}
									<br />
									<a
										href="mailto:Maktur@makedonijaturist.com.mk"
										className="mt-3 inline-block text-white/70 hover:text-accent transition-colors duration-300 cursor-pointer"
									>
										Maktur@makedonijaturist.com.mk
									</a>
								</address>
							</div>
						</div>
					</div>
				</div>

				{/* Bottom section */}
				<div className="mt-14 sm:mt-16 md:mt-20 pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-4">
					<p className="text-xs text-white/45 tracking-wide">
						&copy; {currentYear} {t.footer.companyName}. {t.footer.allRights}
					</p>

					{/* Decorative element */}
					<div className="flex items-center gap-3">
						<span className="h-px w-8 bg-accent/30" />
						<span className="text-xs text-white/40 tracking-[0.2em] uppercase">
							Est. 1963
						</span>
						<span className="h-px w-8 bg-accent/30" />
					</div>
				</div>
			</div>
		</footer>
	);
}
